export type PromptKeys = "CommonIssues" | "Risks" | "ComplainantSentiments" | "Patterns" | "Suggestions";

const prompt: Record<PromptKeys, string> = {
  CommonIssues: `
팩토리얼성수 건물의 고객 불만 데이터가 담긴 엑셀 파일의 '접수내용' 컬럼을 분석해서 고객의 주요 불만 사항을 분석해서 알려줘. 최대 600자 내로 요약해서 알려줘.

**중요 지시사항:**
- 반드시 한국어로만 출력하세요. 영어나 다른 언어는 절대 사용하지 마세요.
- 이 섹션은 반드시 포함되어야 하며, 누락되면 안 됩니다.
- 모든 접수내용은 한국어로 번역하거나 한국어로만 표현하세요.
`,
  Risks: `
고객 서비스 데이터가 포함된 엑셀 파일에서 '접수 내용', '접수자 소속회사' 및 '처리 소요시간' 열을 확인하고, 다음 기준에 따라 위험 요인을 아래와 같이 분석해 주세요:

**계산 방법 (정확성 필수):**
1. 안전 관련 키워드가 포함된 경우: '접수 내용' 컬럼에서 "안전", "위험", "사고", "화재", "누수", "파손" 등의 키워드를 포함한 행을 정확히 카운트하세요. pandas의 str.contains() 함수를 사용하여 정확히 계산하세요.

2. '처리 소요시간' 데이터가 3일을 넘어가는 경우:
   - '처리 소요시간' 컬럼의 값을 숫자로 변환하세요
   - 3일 = 72시간 = 4320분입니다
   - 처리 소요시간 > 4320 (분)인 경우만 카운트하세요
   - 정확히 4320분인 경우는 제외하세요 (초과만 카운트)
   - pandas를 사용하여 정확하게 계산하고, 중복 카운팅하지 마세요

3. 특정 소속회사에서 다수의 접수가 있는 경우:
   - '접수자 소속회사' 컬럼을 기준으로 value_counts()를 사용하여 정확히 집계하세요
   - NULL, 빈 문자열(""), "-" 값은 모두 [소속없음]으로 통일하여 처리하세요
   - 상위 3개 회사명과 정확한 접수건수를 제시하세요

**출력 형식:**
1. 안전 관련 키워드가 포함된 접수 건수: [정확한 건수]건
2. 처리 소요시간이 3일을 초과하는 접수 건수: [정확한 건수]건
3. 상위 3개 소속회사:
   - [회사명1]: [정확한 건수]건
   - [회사명2]: [정확한 건수]건
   - [회사명3]: [정확한 건수]건

**중요 지시사항:**
- 반드시 한국어로만 출력하세요. 영어나 다른 언어는 절대 사용하지 마세요.
- 이 섹션은 반드시 포함되어야 하며, 누락되면 안 됩니다.
- 모든 계산은 pandas를 사용하여 정확하게 수행하고, 결과를 검증하세요.
- 개선 아이디어는 제외해 주세요.
`,
  ComplainantSentiments: `
데이터 준비:
‘팩토리얼성수’ 건물의 고객 불만 데이터가 포함된 Excel 파일 로드
‘접수 내용’ 열에 초점을 맞춤
감정 분석:
각 ‘접수 내용‘에 대해 감정 분석 수행
부정적 감정의 내용 특히 화남과 짜증, 불만을 표현한 내용 식별,
<Emotion>
“화남“: “죽겠어요,너무,힘들어요,더워요,살려주세요,언제되나요?”
“짜증“: “언제 쯤, 그만, 해주세요, 제발”
</Emotion>
감정 분류:
각 내용을 ‘중립‘, ‘화남‘, ‘짜증’ 중 하나로 분류
통계 산출:
전체 문의 중 각 감정 상태의 비율 계산
주요 불만 사항 추출:
화를 유발한 주요 문제점 식별
600자 이내로 요약
결과 정리:
부정적 감정이 표현된 사례 목록 작성
주요 불만 사항 요약을 3건 제공
감정 상태 비율 제공
`,
  Patterns: `
팩토리얼성수 건물의 고객 불만 데이터가 담긴 엑셀 파일의 '접수내용'과 '접수 일시' 열의 데이터를 분석하여 어떤 요일과 시간대에 가장 문의가 많이 접수되는지 파악해줘. 그리고 그 요일과 시간대에 주로 어떤 문의가 접수되는지 500자 내로 요약해서 알려줘. 어떤 문의인지 알려주는 기준은 '접수내용' 열의 데이터를 기준으로 해줘.

**계산 방법 (정확성 필수):**
- '접수 일시' 컬럼을 datetime으로 변환하여 요일과 시간을 추출하세요
- pandas를 사용하여 요일별 접수 건수를 정확히 집계하세요
- 시간대별(0~23시) 접수 건수를 정확히 집계하세요
- 가장 많은 요일과 시간대를 정확히 파악하세요
- 해당 요일과 시간대의 '접수내용'을 분석하여 주요 문의 유형을 파악하세요

**중요 지시사항:**
- 반드시 한국어로만 출력하세요. 영어나 다른 언어는 절대 사용하지 마세요.
- 이 섹션은 반드시 포함되어야 하며, 누락되면 안 됩니다.
- 모든 접수내용은 한국어로 번역하거나 한국어로만 표현하세요.
`,
  Suggestions: `
팩토리얼성수 건물의 고객 불만 데이터가 담긴 엑셀 파일 분석하여, 고객의 불만 사항을 해결하기 위한 구체적이고 실질적인 개선 아이디어를 제안해 주세요. 문제를 해결하기 위한 구체적인 조치와 구체적인 방법을 900자 내로 요약해서 알려주세요. 작성한 내용 중에 주요 불만 사항은 삭제해 주세요.

**중요 지시사항:**
- 반드시 한국어로만 출력하세요. 영어나 다른 언어는 절대 사용하지 마세요.
- 이 섹션은 반드시 포함되어야 하며, 누락되면 안 됩니다.
- 모든 내용은 한국어로만 작성하세요.
`,
};

export default prompt;
